import { AlertCircle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PricingCard } from "@/components/pricing-card";
import { products } from "@/lib/stripe-config";
import { useNavigate } from "react-router-dom";
import { MobilePricingCard } from "@/components/mobile-pricing-card";
import { useState, useEffect } from "react";
import { useCredits } from "@/hooks/use-credits";

interface CreditAlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showPricingCards?: boolean;
}

export function CreditAlertDialog({
  open,
  onOpenChange,
  showPricingCards = false,
}: CreditAlertDialogProps) {
  const navigate = useNavigate();
  const { usedCredits } = useCredits();
  const [isMobile, setIsMobile] = useState(false);

  // 구독 상품 목록
  const subscriptionProducts = products.filter(
    (p) => p.mode === "subscription"
  );
 
  const mostExpensiveSubscription = subscriptionProducts.length
    ? subscriptionProducts.reduce((prev, curr) =>
        prev.price > curr.price ? prev : curr
      )
    : null;
  // 보여줄 상품 결정: usedCredits > 10 ? 비싼 상품 : 저렴 상품
  const subscriptionToShow =
    usedCredits !== null && usedCredits > 10
      ? mostExpensiveSubscription
      : mostExpensiveSubscription;

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const handleViewAllPlans = () => {
    onOpenChange(false);
    navigate("/pricing");
  };

  return (
    <>
      {/* Desktop version */}
      <AlertDialog open={open && !isMobile} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-md md:max-w-md lg:max-w-md ">
          <AlertDialogHeader className="mb-2 md:mb-4 space-y-2">
            <AlertDialogTitle className="flex items-center text-xl md:text-2xl text-destructive gap-3">
              <AlertCircle className="h-6 w-6 flex-shrink-0" />
              <span>크레딧 부족</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-relaxed">
              대화를 계속하기 위한 크레딧이 부족합니다.
              <br className="hidden md:block" />
              인스타챗에 가입하고 검열없는 AI 대화 시뮬레이터를 경험하세요.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {showPricingCards && subscriptionToShow && (
            <PricingCard
              title={subscriptionToShow.name}
              description={subscriptionToShow.description}
              price={subscriptionToShow.price}
              priceId={subscriptionToShow.priceId}
              features={subscriptionToShow.features}
              popular={subscriptionToShow.popular}
              mode={subscriptionToShow.mode}
              credits={subscriptionToShow.credits}
              billingPeriod="주"
            />
          )}

          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-4 mt-6 md:mt-8 pt-2">
            <AlertDialogCancel className="mt-2 sm:mt-0 h-12 text-base">
              닫기
            </AlertDialogCancel>
            <Button
              className="gradient-button w-full sm:w-auto h-12 text-base font-medium"
              onClick={() => {
                onOpenChange(false);
                navigate("/pricing");
              }}
            >
              <Zap className="mr-2 h-5 w-5" />
              모든 요금제 보기
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile version */}
      {subscriptionToShow && (
        <MobilePricingCard
          isOpen={open && isMobile}
          onClose={() => onOpenChange(false)}
          onViewAllPlans={handleViewAllPlans}
          product={{
            ...subscriptionToShow,
            credits: subscriptionToShow.credits ?? 0,
          }}
        />
      )}
    </>
  );
}
